import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { supabase } from '../../utils/supabaseClient';
import { trackEvent } from '../../utils/posthog';

interface RazorpayButtonProps {
  amount: number;
  onSuccess: (paymentId: string) => void;
  onError: (error: any) => void;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const RazorpayButton: React.FC<RazorpayButtonProps> = ({ amount, onSuccess, onError }) => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const verifyPayment = async (paymentId: string) => {
    try {
      // Track payment verification attempt
      trackEvent('payment_verification_attempt', {
        payment_id: paymentId,
        amount: amount
      });

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_id: paymentId,
          user_id: user?.id
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to verify payment');
      }

      // Force refresh the session to get updated metadata
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;

      if (!session?.user.user_metadata?.is_premium) {
        throw new Error('Premium status not updated');
      }

      // Track successful payment verification
      trackEvent('payment_verification_success', {
        payment_id: paymentId,
        amount: amount
      });

      return true;
    } catch (error) {
      console.error('Payment verification error:', error);
      
      // Track payment verification failure
      trackEvent('payment_verification_error', {
        error_message: error.message,
        payment_id: paymentId,
        amount: amount
      });
      
      throw error;
    }
  };

  const handlePayment = () => {
    // Track payment initiation
    trackEvent('payment_initiated', {
      amount: amount,
      currency: 'INR',
      user_id: user?.id
    });

    const options = {
      key: import.meta.env.VITE_RAZORPAY_KEY_ID,
      amount: amount * 100, // Amount in paise
      currency: 'INR',
      name: '11th Admissions',
      description: 'Premium Subscription',
      handler: async function(response: any) {
        try {
          // Track payment completion
          trackEvent('payment_completed', {
            payment_id: response.razorpay_payment_id,
            amount: amount
          });

          await verifyPayment(response.razorpay_payment_id);
          showToast('Payment successful! Premium features activated.', 'success');
          onSuccess(response.razorpay_payment_id);
        } catch (error) {
          // Error is already tracked in verifyPayment
          showToast('Failed to verify payment. Please contact support.', 'error');
          onError(error);
        }
      },
      prefill: {
        name: user?.name || '',
        email: user?.email || '',
      },
      theme: {
        color: '#7B4AE2'
      },
      modal: {
        ondismiss: function() {
          // Track payment cancellation
          trackEvent('payment_cancelled', {
            amount: amount
          });
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  return (
    <button
      onClick={handlePayment}
      className="w-full bg-[#7B4AE2] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#6A3ACF] transition-colors"
    >
      Pay ₹{amount}
    </button>
  );
};

export default RazorpayButton;